import React from 'react';
import DownloadInvoiceButton from './DownloadInvoiceButton';
import { useTranslation } from 'react-i18next';
import { useCountryConfig } from '../hooks/useCountryConfig';
import { getCurrencyTextLabel } from '../utils/currencyUtils';

const SuccessScreenWithDownload: React.FC = () => {
  const { t } = useTranslation();
  const countryConfig = useCountryConfig();
  const currencyLabel = getCurrencyTextLabel(
    countryConfig,
    t('currency.egyptSymbol'),
    'ريال'
  );

  // بيانات تجريبية
  const orderData = {
    orderNumber: '9028',
    date: 'Wed, May 27, 2020',
    time: '9:27:53 AM',
    customerName: 'Malicious Guest',
    customerPhone: '0570577722',
    branch: 'الفرع الرئيسي',
    paymentMethod: 'الدفع الإلكتروني',
    items: [
      { name: 'غير محدود', quantity: 1, price: 9.0 },
      { name: 'شاورما', quantity: 2, price: 9.0 },
      { name: 'فول', quantity: 2, price: 45.0 }
    ],
    subtotal: 84.0,
    tax: 12.6,
    total: 84.0
  };

  const sar = (n: number) =>
    `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n || 0)} ${currencyLabel}`;

  const fmtDate = (s: string) => {
    try {
      const dateLocale = countryConfig.country.code ? `ar-${countryConfig.country.code}` : 'ar-SA'
      return new Intl.DateTimeFormat(dateLocale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(new Date(s));
    } catch {
      return s;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4" dir="rtl">
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-extrabold text-gray-900 mt-4 mb-4 text-center">
          تم إرسال طلبك بنجاح
        </h1>

        {/* الفاتورة - نفس تصميم SuccessScreen */}
        <div
          id="invoice-content" // ID مهم للتحميل
          className="bg-white rounded-xl shadow-md p-5 text-right mb-4"
        >
          <div className="text-xs text-gray-500 text-center mb-2">
            {fmtDate('2020-05-27T09:27:53.000Z')}
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg py-2 text-center mb-4">
            <p className="text-xs text-gray-500">رقم الطلب</p>
            <p className="text-2xl font-bold text-gray-600">
              {orderData.orderNumber}
            </p>
          </div>

          {/* بيانات العميل */}
          <div className="space-y-2 text-sm mb-4">
            <div className="flex justify-between">
              <span className="text-gray-600">اسم العميل</span>
              <span>{orderData.customerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">رقم هاتف العميل</span>
              <span>{orderData.customerPhone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">الفرع</span>
              <span>{orderData.branch}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">طريقة الدفع</span>
              <span>{orderData.paymentMethod}</span>
            </div>
          </div>

          {/* جدول المنتجات */}
          <div className="pt-1 mt-1">
            <div className="flex justify-between text-xs font-semibold text-gray-700 mb-1">
              <span className="w-1/3 text-right">المنتجات</span>
              <span className="w-1/3 text-center">الكمية</span>
              <span className="w-1/3 text-left">السعر</span>
            </div>

            {orderData.items.map((item, i) => (
              <div key={i} className="py-2">
                <div className="flex justify-between items-start">
                  <span className="w-1/3 text-right text-gray-800 font-medium">
                    {item.name}
                  </span>
                  <span className="w-1/3 text-center font-medium">{item.quantity}</span>
                  <span className="w-1/3 text-left text-gray-600 font-medium">
                    {sar(item.price * item.quantity)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          <hr className="border-gray-300 mt-2" />

          {/* الإجماليات */}
          <div className="mt-2 pt-1 text-sm space-y-1">
            <div className="flex justify-between">
              <span>المجموع الفرعي</span>
              <span className="text-gray-600">{sar(orderData.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>الضريبة %15</span>
              <span className="text-gray-600">{sar(orderData.tax)}</span>
            </div>
            <p className="text-center text-gray-400 mt-1">----------------------------------------------------------------------------------------------------------------------------------------</p>
            <div className="flex justify-between font-bold pt-1">
              <span>الإجمالي الكلي</span>
              <span className="text-gray-700">{sar(orderData.total)}</span>
            </div>
          </div>

          <p className="text-[11px] text-gray-500 mt-2 text-start">
            ملاحظة: الفاتورة الضريبية سيتم إرسال الطلب من متجر <br />
            استرجاع مبلغ الضريبة عند طلب فاتورة ضريبية في حال عدم استلام الطلب
          </p>
        </div>

        {/* زر التحميل */}
        <DownloadInvoiceButton
          targetElementId="invoice-content"
          orderNumber={orderData.orderNumber}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
        >
          تحميل الفاتورة PDF
        </DownloadInvoiceButton>
      </div>
    </div>
  );
};

export default SuccessScreenWithDownload;
